import React, { useEffect, useState } from "react";
import { useApi } from "../../hooks/useApi";
import { useRBAC } from "../../hooks/useRBAC";
import { fetchTracked, formatMoney, laneLabel, untrackChain } from "./fusionApi";

const TYPE_LANE = { Identity: "Identity", Device: "Device", AiAsset: "AI", Control: "GRC", Vendor: "TPRM", BusinessUnit: "BusinessUnit", Deal: "Deal" };

/**
 * The organization's tracked chains (FUSION.md §11 F3), shown on the Fusion dashboard view to users with
 * full access (FusionPanel hides it in Partial view). Each row opens its chain; an open exposure-rise
 * alert is flagged. A risk manager (the same gate as the chain detail's Stop tracking) can stop tracking
 * a row here too, because a row whose anchor is no longer built (a Deal or Business Unit) has no chain
 * detail to open.
 */
const FusionTrackedList = ({ onOpen, refreshNonce }) => {
  const apiCall = useApi();
  const { canManageOrganizationRisks } = useRBAC();
  const canManage = canManageOrganizationRisks();
  const [rows, setRows] = useState(null);
  const [reload, setReload] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    fetchTracked(apiCall).then((r) => { if (live) setRows(r || []); }).catch(() => { if (live) setRows([]); });
    return () => { live = false; };
  }, [apiCall, refreshNonce, reload]);

  const stop = async (id) => {
    setBusyId(id);
    setError(null);
    try {
      await untrackChain(apiCall, id);
      setReload((n) => n + 1);
    } catch {
      setError("Tracking could not be stopped.");
    } finally {
      setBusyId(null);
    }
  };

  if (!rows || rows.length === 0) return null;

  return (
    <div className="mt-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-content-secondary mb-1.5">Tracked chains</div>
      {rows.map((t) => {
        const change = t.lastMean != null ? Number(t.lastMean) - Number(t.baselineMean) : null;
        return (
          <div key={t.id} className="flex items-center gap-2 mb-1.5">
            <button
              onClick={() => onOpen(t.anchorType, t.anchorId)}
              className="flex-1 min-w-0 flex items-center gap-3 text-left rounded-lg border border-border bg-theme-secondary px-3 py-2 hover:border-indigo-200"
            >
              <span className="flex-1 min-w-0">
                <span className="font-semibold text-content-primary">{t.anchorLabel}</span>
                <span className="ml-2 text-xs text-content-muted">{laneLabel(TYPE_LANE[t.anchorType] || t.anchorType)}</span>
                {!t.lastAvailable && <span className="ml-2 text-xs text-content-muted">not available at the last snapshot</span>}
              </span>
              {t.activeAlertSeverity && (
                <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-red-100 text-red-700">Exposure rise · {t.activeAlertSeverity}</span>
              )}
              <span className="text-xs text-content-secondary whitespace-nowrap">
                {formatMoney(t.baselineMean)} → <b className="text-content-primary">{formatMoney(t.lastMean)}</b>
                {change !== null && change !== 0 && <span className={change > 0 ? "text-red-600" : "text-green-700"}> ({change > 0 ? "+" : "−"}{formatMoney(Math.abs(change))})</span>}
              </span>
            </button>
            {canManage && (
              <button
                onClick={() => stop(t.id)}
                disabled={busyId === t.id}
                className="text-xs px-2.5 py-1 rounded-lg border border-border bg-theme-secondary text-content-secondary whitespace-nowrap"
              >
                Stop tracking
              </button>
            )}
          </div>
        );
      })}
      {error && <div className="text-xs text-red-600">{error}</div>}
    </div>
  );
};

export default FusionTrackedList;
